/* eslint-disable react/jsx-props-no-spreading */
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Button, LoadingOverlay, Select, Space, Stack, Text } from '@mantine/core';
import { useLingui } from '@lingui/react';
import { useForm } from '@mantine/form';
import { closeAllModals, openModal } from '@mantine/modals';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconCalendarEvent, IconChevronDown, IconFileDescription } from '@tabler/icons';
import { EventCustomerSelect, GeneralCustomerSelect } from '../../../Atoms/inputs/CustomerSelect';
import DocumentTemplateSelect from '../../../Atoms/inputs/DocumentTemplateSelect';
import { DataS } from '../../../Service/DataS';
import { createDocument } from '../../../Service/restapi/documentService';
import { ToastS } from '../../../Service/ToastS';
import BookitupDocument, { BookitupDocumentType } from '../../../Types/BookitupDocument';
import { TestS } from '../../../TestS';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { hasSuitableLicence } from '../../../Service/helper/LicenceService';
import { LicenceRestriction, LicenceS } from '../../../Service/LicenceS';
import { ConstantS } from '../../../Service/ConstantS';
import { MixpanelS } from '../../../Service/MixpanelS';
import { DocumentS } from '../../../Templates/Documents/DocumentS';
import BookingEvent from '../../../Types/Event';
import { EventS } from '../../../Service/EventS';

interface Props {
  documentId?: number;
  initialDocumentType?: BookitupDocumentType;
  initialCustomerId?: number;
  eventParticipationContext?: unknown;
  copyingDocument?: boolean;
  eventId?: number | null;
  refresh?: () => void;
  finalInvoice?: boolean;
  openLicenceModal: (licenceRestriction: LicenceRestriction) => void;
  interceptViolation?: (httpResponse: Response) => void;
  provideEventSelect?: boolean;
  disableCustomerSelection?: boolean;
}

export const openCreateDocumentModal = (props: Props) =>
  openModal({
    title: (
      <Text weight="bolder" size="xl" sx={{ lineHeight: 1.2 }}>
        Vorlage wählen
      </Text>
    ),
    children: <CreateDocumentModal {...props} />,
  });

const CreateDocumentModal: FC<Props> = ({
  documentId,
  initialDocumentType = 'OFFER',
  initialCustomerId,
  eventParticipationContext,
  copyingDocument,
  eventId: initialEventId,
  refresh,
  finalInvoice,
  openLicenceModal,
  interceptViolation,
  provideEventSelect = false,
  disableCustomerSelection = false,
}) => {
  const form = useForm({
    initialValues: {
      documentType: initialDocumentType,
      templateName: '',
      customerId: initialCustomerId,
      eventId: initialEventId,
    },
    validate: {
      customerId: (value?: number) => (value && !isNaN(value) ? null : 'Bitte Kunden auswählen'),
    },
  });
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const { documentType, eventId } = form.values;
  const [loading, setLoading] = useState<boolean>(false);
  const { licence } = useJwtClaims();
  const [bookingEvents, setBookingEvents] = useState<BookingEvent[]>([]);
  const { length } = bookingEvents;
  const emptyEvents = !loading && length === 0;

  useEffect(() => {
    if (initialCustomerId && provideEventSelect) {
      setLoading(true);
      EventS.fetchEventsByCustomerParticipations(initialCustomerId)
        .then(setBookingEvents)
        .finally(() => setLoading(false));
    }
  }, [initialCustomerId, provideEventSelect]);

  const onSubmit = () => {
    if (documentType === 'PARTIAL_INVOICE' && !LicenceS.hasSuitableLicence('PRO', licence)) {
      openLicenceModal(LicenceS.Restrictions.SAVE_PARTIAL_INVOICE);
      return;
    }
    const creditNoteRestriction = LicenceS.Restrictions.SAVE_CREDIT_NOTE;
    if (
      documentType === 'CREDIT_NOTE' &&
      !LicenceS.hasSuitableLicence(creditNoteRestriction.requiredLicence, licence)
    ) {
      openLicenceModal(creditNoteRestriction);
      return;
    }
    const { templateName } = form.values;
    if (templateName !== '' && templateName !== 'Standard' && !hasSuitableLicence('PRO', licence)) {
      openLicenceModal(LicenceS.Restrictions.MULTIPLE_DOCUMENT_TEMPLATE);
      return;
    }
    setLoading(true);

    const onCreate = (newDocument: BookitupDocument) => {
      const { id, documentType } = newDocument;
      closeAllModals();
      MixpanelS.track(ConstantS.TrackingEvents.DocumentCreated, { documentType });
      navigate(eventId ? `/events/${eventId}/documents/${id}` : `/documents/${id}`);
      refresh?.();
    };
    const { customerId } = form.values;
    if (copyingDocument && documentId) {
      DocumentS.copyDocument(documentId, documentType, templateName, customerId, finalInvoice, interceptViolation)
        .then((resJSON) => {
          if (resJSON) {
            onCreate(resJSON);
          }
        })
        .finally(() => setLoading(false));
    } else {
      createDocument(customerId, templateName, eventId, documentType)
        .then((resp) => {
          if (resp.ok) {
            resp.json().then(onCreate);
          } else if (interceptViolation) {
            interceptViolation(resp);
          } else {
            ToastS.error('err', 'Dokument kann derzeit nicht erstellt werden. Bitte versuch es später.');
          }
        })
        .finally(() => setLoading(false));
    }
  };

  const includeReminder = initialDocumentType === 'REMINDER' && documentId !== undefined;
  const includeCreditNote = !documentId;

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack spacing="sm" mt="xl" sx={{ position: 'relative' }}>
        <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} overlayOpacity={0.3} />
        <Select
          icon={<IconFileDescription size={16} />}
          label="Dokumentenart"
          data={DataS.getDocumentTypeOptions(includeReminder, includeCreditNote)}
          styles={{ rightSection: { pointerEvents: 'none' } }}
          rightSection={<IconChevronDown size={14} />}
          rightSectionWidth={25}
          {...form.getInputProps('documentType')}
          onChange={(value) => {
            form.setFieldValue('documentType', value as BookitupDocumentType);
            form.setFieldValue('templateName', '');
          }}
        />
        <DocumentTemplateSelect documentType={documentType} {...form.getInputProps('templateName')} />
        {provideEventSelect && (
          <Select
            label="Event"
            icon={<IconCalendarEvent size={16} />}
            data={DataS.getEventOptions(bookingEvents)}
            styles={{ rightSection: { pointerEvents: 'none' } }}
            rightSection={<IconChevronDown size={14} />}
            rightSectionWidth={25}
            nothingFound={
              emptyEvents && (
                <Text>
                  Es wurde kein Event zu diesem Kontakt gefunden. Bitte füge den Kontakt erst einem Event hinzu.
                </Text>
              )
            }
            {...form.getInputProps('eventId')}
          />
        )}

        {eventParticipationContext && JSON.stringify(eventParticipationContext) !== '{}' ? (
          <EventCustomerSelect
            initialValue={initialCustomerId && initialCustomerId.toString()}
            eventParticipationContext={eventParticipationContext}
            disabled={disableCustomerSelection}
            {...form.getInputProps('customerId')}
          />
        ) : (
          <GeneralCustomerSelect
            initialValue={initialCustomerId && initialCustomerId.toString()}
            disabled={disableCustomerSelection}
            {...form.getInputProps('customerId')}
          />
        )}
      </Stack>
      <Space h="md" />
      <Button fullWidth mt="xl" mb="md" type="submit" disabled={loading} data-testid={TestS.Documents.Create.SAVE}>
        {i18n._('common.apply')}
      </Button>
      <Text color="dimmed" align="center" sx={{ cursor: 'pointer' }} onClick={() => closeAllModals()}>
        {i18n._('actions.cancel')}
      </Text>
    </form>
  );
};

export default CreateDocumentModal;
